import type CardClient from '@atlaskit/link-provider/client';
import type { CardStore } from '@atlaskit/linking-common/store';

import { resolveSmartLinkByUrl } from './resolveSmartLinkByUrl.ts';
import type { EnhancedSmartLink } from './smartLinkTypes.ts';

/**
 * Hydrates the URLs the graph returned for a page of incoming links into
 * `EnhancedSmartLink`s, through the same store-then-ORS resolution the outgoing
 * links use (`resolveSmartLinkByUrl`). The graph supplies nothing but the url; all
 * display metadata comes from the resolved response.
 *
 * Unresolved URLs are dropped rather than rendered bare: unlike an outgoing link,
 * whose url is on the page in front of the user, an inbound row with no title says
 * nothing. Access-restricted and unauthorized entities still resolve — they carry
 * an `access` status and their provider's connect prompt — so they are kept.
 *
 * Links are de-duplicated by identity — the resolved `ari`, falling back to the url
 * when ORS gave us none. The urls are already unique, so this only collapses rows
 * whose different urls resolve to the same entity (e.g. a comment whose url ORS
 * resolves to its parent page). Order follows the incoming url order.
 */
export async function hydrateIncomingLinks(
	urls: string[],
	store: { getState: () => CardStore },
	cardClient: CardClient,
): Promise<EnhancedSmartLink[]> {
	const resolved = await Promise.all(
		urls.map((url, index) => resolveSmartLinkByUrl(url, index, store, cardClient)),
	);

	const links: EnhancedSmartLink[] = [];
	const seen = new Set<string>();

	resolved.forEach(({ link, isResolved }) => {
		const key = link.ari ?? link.url;
		if (!isResolved || seen.has(key)) {
			return;
		}
		seen.add(key);
		links.push(link);
	});

	return links;
}
